import {
  Button,
  ButtonVariantEnum,
} from "@booking/components/atoms/forms/Button";
import Dropdown, {
  DropdownEventEnum,
} from "@booking/components/compounds/forms/Dropdown";
import TextInput, {
  TextInputTypeEnum,
  TextInputEventEnum,
  TextInputAutocompleteEnum,
} from "@booking/components/compounds/forms/TextInput";
import CheckboxGroup from "@booking/components/containers/forms/CheckboxGroup";
import { ButtonActionEnum, SizeEnum } from "@booking/enums";
import { useSubscribe } from "@booking/hooks/usePubSub";
import React, { useState } from "react";

import { DialingCode } from "@/components/compounds/forminputs/DialingCode";
import { CheckboxInputEventEnum } from "@/components/compounds/forms/CheckboxInput";
import { ColorEnum } from "@/enums";

import {
  CALL_TO_ACTION_TEXT,
  PHONE_LINK,
  PHONE_NUMBER_DISPLAY,
} from "../utils/constants";
import { createUpdateControl } from "../utils/formHelpers";
import styles from "./CreateYourOwnForm.module.css";

interface CreateYourOwnPersonalFormProps {
  onSubmit: (personalDetails: CreateYourOwnPersonalFormData) => void;
  onBack?: () => void;
  initialData?: Partial<CreateYourOwnPersonalFormData>;
  isSubmitting?: boolean;
  summaryInfo?: {
    date: string;
    nights: string;
    rounds: string;
    venueName?: string;
  };
}

export interface CreateYourOwnPersonalFormData {
  title: string;
  first_name: string;
  last_name: string;
  email: string;
  dialing_code: string;
  mobile: string;
  receive_offers: boolean;
}

const initialFormData: CreateYourOwnPersonalFormData = {
  title: "Mr",
  first_name: "",
  last_name: "",
  email: "",
  dialing_code: "+44",
  mobile: "",
  receive_offers: true,
};

const titleOptions = [
  { value: "Mr", text: "Mr" },
  { value: "Mrs", text: "Mrs" },
  { value: "Miss", text: "Miss" },
  { value: "Ms", text: "Ms" },
  { value: "Dr", text: "Dr" },
];

// Input field names
const INPUTS = {
  TITLE: "title",
  FIRST_NAME: "first_name",
  LAST_NAME: "last_name",
  EMAIL: "email",
  DIALING_CODE: "dialing_code",
  MOBILE: "mobile",
  RECEIVE_OFFERS: "receive_offers",
};

function CreateYourOwnPersonalForm(props: CreateYourOwnPersonalFormProps) {
  const {
    onSubmit,
    onBack,
    initialData = {},
    isSubmitting = false,
    summaryInfo,
  } = props;

  const [formData, setFormData] = useState<CreateYourOwnPersonalFormData>({
    ...initialFormData,
    ...initialData,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateForm = (data: Partial<CreateYourOwnPersonalFormData>) => {
    setFormData({ ...formData, ...data });

    // Clear errors for updated fields
    const updatedFields = Object.keys(data);
    if (updatedFields.some((field) => errors[field])) {
      const newErrors = { ...errors };
      updatedFields.forEach((field) => {
        if (newErrors[field]) {
          delete newErrors[field];
        }
      });
      setErrors(newErrors);
    }
  };

  const updateControl =
    createUpdateControl<CreateYourOwnPersonalFormData>(updateForm);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim() || !emailRegex.test(formData.email)) {
      newErrors.email = "Valid email is required";
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  function isControlInvalid(key: keyof CreateYourOwnPersonalFormData) {
    return !!errors[key];
  }

  // Subscribe to form events
  useSubscribe(TextInputEventEnum.CHANGE, updateControl, [
    INPUTS.FIRST_NAME,
    INPUTS.LAST_NAME,
    INPUTS.EMAIL,
    INPUTS.MOBILE,
  ]);

  useSubscribe(DropdownEventEnum.CHANGE, updateControl, [INPUTS.TITLE]);

  useSubscribe(CheckboxInputEventEnum.CHANGE, updateControl, [
    INPUTS.RECEIVE_OFFERS,
  ]);

  useSubscribe(DropdownEventEnum.CHANGE, updateControl, [INPUTS.DIALING_CODE]);

  const renderSummary = () => {
    if (!summaryInfo) return null;

    return (
      <div className={styles.summaryHeader}>
        <h3>{summaryInfo.venueName || "Golf Break"}</h3>
        <p className={styles.summaryDetails}>
          {summaryInfo.nights} night, {summaryInfo.rounds} round on{" "}
          {summaryInfo.date}
          {onBack && (
            <Button variant={ButtonVariantEnum.PLAIN} onClick={onBack}>
              (change)
            </Button>
          )}
        </p>
      </div>
    );
  };

  return (
    <>
      {renderSummary()}
      <div className={styles.formWrapper}>
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.formInner}>
            <div>
              <TextInput
                autocomplete={TextInputAutocompleteEnum.EMAIL}
                invalid={isControlInvalid(
                  INPUTS.EMAIL as keyof CreateYourOwnPersonalFormData,
                )}
                name={INPUTS.EMAIL}
                placeholder={"E.g. suzann.pettersen@yourgolftravel.com"}
                required
                type={TextInputTypeEnum.EMAIL}
                value={formData.email}
                label="Email"
              />
              {errors.email && (
                <div className={styles.errorMessage}>{errors.email}</div>
              )}
            </div>

            <Dropdown
              name={INPUTS.TITLE}
              options={titleOptions}
              required
              value={formData.title}
              label="Title"
            />

            <div>
              <TextInput
                autocomplete={TextInputAutocompleteEnum.GIVEN_NAME}
                invalid={isControlInvalid(
                  INPUTS.FIRST_NAME as keyof CreateYourOwnPersonalFormData,
                )}
                label="First name"
                name={INPUTS.FIRST_NAME}
                placeholder={"E.g. Suzann"}
                required
                value={formData.first_name}
              />
              {errors.first_name && (
                <div className={styles.errorMessage}>{errors.first_name}</div>
              )}
            </div>

            <div>
              <TextInput
                autocomplete={TextInputAutocompleteEnum.FAMILY_NAME}
                invalid={isControlInvalid(
                  INPUTS.LAST_NAME as keyof CreateYourOwnPersonalFormData,
                )}
                name={INPUTS.LAST_NAME}
                placeholder={"E.g. Pettersen"}
                required
                value={formData.last_name}
                label="Last name"
              />
              {errors.last_name && (
                <div className={styles.errorMessage}>{errors.last_name}</div>
              )}
            </div>

            <div className={styles.formRow}>
              <div className={styles.formLabel}>
                <label htmlFor={INPUTS.MOBILE}>Mobile number</label>
              </div>
              <div className={styles.phoneInputGroup}>
                <DialingCode
                  id={INPUTS.DIALING_CODE}
                  validityFn={() =>
                    isControlInvalid(
                      INPUTS.DIALING_CODE as keyof CreateYourOwnPersonalFormData,
                    )
                  }
                  value={formData.dialing_code}
                />

                <TextInput
                  autocomplete={TextInputAutocompleteEnum.TEL}
                  className={styles.telephone}
                  invalid={isControlInvalid(
                    INPUTS.MOBILE as keyof CreateYourOwnPersonalFormData,
                  )}
                  name={INPUTS.MOBILE}
                  noLabel
                  placeholder={"0000 000000"}
                  required
                  type={TextInputTypeEnum.TEL}
                  value={formData.mobile}
                  label="Mobile number"
                />
              </div>
              {errors.mobile && (
                <div className={styles.errorMessage}>{errors.mobile}</div>
              )}
            </div>
          </div>

          <CheckboxGroup
            color={ColorEnum.SLATE}
            data={[
              {
                children: [
                  <p key="signup">
                    Sign up to get member offers from us. By signing up you
                    agree to our{" "}
                    <a
                      href="https://www.yourgolftravel.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>,
                ],
                checked: formData.receive_offers,
                name: INPUTS.RECEIVE_OFFERS,
                value: "on",
              },
            ]}
            legend={"Email signup"}
            name={"email-signup"}
            noBorder
            noLegend
          />

          <div className={styles.formActions}>
            <Button
              color={ColorEnum.DARKGREEN}
              disabled={isSubmitting}
              size={SizeEnum.LARGE}
              type={ButtonActionEnum.SUBMIT}
              variant={ButtonVariantEnum.SOLID}
            >
              {isSubmitting ? "Sending..." : "ENQUIRE NOW"}
            </Button>
          </div>

          <div className={styles.callInfo}>
            <p>
              {CALL_TO_ACTION_TEXT}{" "}
              <a href={PHONE_LINK}>{PHONE_NUMBER_DISPLAY}</a> from 8:30AM today
            </p>
          </div>
        </form>
      </div>
    </>
  );
}

export default CreateYourOwnPersonalForm;
export { CreateYourOwnPersonalForm, type CreateYourOwnPersonalFormProps };
